import { SentryError } from '@ifixit/sentry';
import { webApiClient } from 'Shared/web_api_client';

const MAX_FAILURES = 5;

export default class NotificationsClient {
   // Bumped when a mark-read request starts and again when it finishes, so a
   // poll that overlapped one can tell its response may predate the write.
   static writes = 0;

   static running = new Set();

   constructor({
      onNotification,
      onMarkRead,
      onMarkAllRead,
      onUnreadCount = () => {},
      includeHtml = false,
   }) {
      this.onNotification = onNotification;
      this.onMarkRead = onMarkRead;
      this.onMarkAllRead = onMarkAllRead;
      this.onUnreadCount = onUnreadCount;

      this.endpoint = '/api/2.0/notifications/recent' + (includeHtml ? '?html=1' : '');
      this.minInterval = 15_000;
      this.maxInterval = 60_000 * 30;

      this.interval = this.minInterval;
      this.timer = null;
      this.stopped = true;
      this.polling = false;
      this.pollAgain = false;
      this.errorStreak = 0;
      this.unreadSignatures = new Set(App.unreadNotificationSignatures);

      this._onVisibilityChange = this._onVisibilityChange.bind(this);
   }

   start() {
      this.stopped = false;
      NotificationsClient.running.add(this);
      document.addEventListener('visibilitychange', this._onVisibilityChange);
      // The page was rendered with the current notifications
      this._schedule();
   }

   stop() {
      this.stopped = true;
      NotificationsClient.running.delete(this);
      clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
   }

   _schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this._poll(), this.interval);
   }

   _onVisibilityChange() {
      if (document.visibilityState !== 'visible') {
         return;
      }

      this.interval = this.minInterval;
      if (!this.polling) {
         clearTimeout(this.timer);
         this._poll();
      }
   }

   _refresh() {
      this.interval = this.minInterval;
      if (this.polling) {
         // The poll in flight may have been answered before the write landed
         this.pollAgain = true;
         return;
      }
      clearTimeout(this.timer);
      this._poll();
   }

   async _poll() {
      if (this.stopped) {
         return;
      }

      this.polling = true;
      const writesBefore = NotificationsClient.writes;
      let status;
      try {
         const res = await fetch(this.endpoint);
         status = res.status;
         if (status === 401 || status === 403) {
            // Logged out, e.g. in another tab
            this.stop();
            return;
         }
         if (!res.ok) {
            throw new SentryError(`Notification polling got ${status}`);
         }

         const { notifications, unread } = await res.json();
         this.errorStreak = 0;

         // A response that overlapped a mark-read may be stale, so skip it and
         // check again soon
         if (NotificationsClient.writes !== writesBefore || this._sync(notifications, unread)) {
            this.interval = this.minInterval;
         } else {
            const growthFactor = document.visibilityState === 'visible' ? 1.3 : 2;
            this.interval = Math.min(this.interval * growthFactor, this.maxInterval);
         }
      } catch {
         this.errorStreak++;
         this.interval = Math.min(this.minInterval * 2 ** this.errorStreak, this.maxInterval);

         // Network failures (offline, asleep) keep backing off and never report
         if (status !== undefined && this.errorStreak >= MAX_FAILURES) {
            this.stop();
            throw new SentryError(`Notification polling failed ${MAX_FAILURES} times in a row`, {
               extra: { status },
            });
         }
      } finally {
         this.polling = false;
         if (this.pollAgain && !this.stopped) {
            this.pollAgain = false;
            this._poll();
         } else if (!this.stopped) {
            this._schedule();
         }
      }
   }

   /**
    * Applies the server's unread state, standing in for the events the
    * realtime server used to push (including to other tabs).
    * Returns true if any new notifications were shown.
    */
   _sync(notifications, count) {
      // Oldest first so the newest ends up rendered at the top
      const added =
         [...notifications]
            .reverse()
            .filter(n => !this.unreadSignatures.has(n.signature) && this.onNotification(n)).length >
         0;

      const current = new Set(notifications.map(n => n.signature));
      if (count === 0) {
         if (this.unreadSignatures.size > 0) {
            this.onMarkAllRead();
         }
         this.unreadSignatures = current;
      } else if (notifications.length >= count) {
         // Only a complete list proves a missing notification was read, rather
         // than pushed past the server's recent limit.
         const read = [...this.unreadSignatures].filter(sig => !current.has(sig));
         if (read.length > 0) {
            this.onMarkRead(read);
         }
         this.unreadSignatures = current;
      } else {
         // Keep tracking the ones pushed past the limit so a later complete
         // list can still mark them read
         this.unreadSignatures = new Set([...this.unreadSignatures, ...current]);
      }
      this.onUnreadCount(count);

      return added;
   }

   /**
    * Polls every running client now. For writes that skip markRead and
    * markAllRead, like the shared header bell's, so the header's count doesn't
    * wait for the next poll.
    */
   static refreshAll() {
      NotificationsClient.running.forEach(client => client._refresh());
   }

   static markRead(id, options = {}) {
      // The signature is already URL-encoded
      return NotificationsClient._write(
         client => client.get(`notifications?ids=${id}`, { statName: 'notification-mark-read' }),
         options
      );
   }

   static markAllRead(options = {}) {
      return NotificationsClient._write(
         client => client.delete('notifications/all', 'notification-mark-all-read'),
         options
      );
   }

   static async _write(send, { onSuccess }) {
      NotificationsClient.writes++;
      let response;
      try {
         response = await send(await webApiClient());
      } finally {
         NotificationsClient.writes++;
      }
      onSuccess?.(response);
   }
}
